import { useCallback, useEffect, useRef, useState } from 'react'
import { assistant, type AssistantAnswer } from '../api/client'
import { isNativeApp } from '../native'
import { Prose } from './Prose'

/*
  The Boardium Assistant: the launcher in the bottom-right corner, and the panel it opens.

  It answers questions about how to use the software from a knowledge base on the server
  that describes the product as it ships. It cannot see this company's agenda, meetings,
  figures or files and cannot change anything, which is said out loud in the empty panel
  rather than left for somebody to find out by asking it to move Friday's meeting.

  ── The thread lives here ───────────────────────────────────────────────────

  In component state, posted back whole on every turn. There is no session to expire,
  closing the tab ends the conversation, and the server keeps a row only for as long as it
  takes to answer (pruned after a week). It also means the thread survives walking between
  screens, because this is mounted in the shell for the life of the session.

  ── Why it polls ────────────────────────────────────────────────────────────

  The web tier may not call Anthropic (SPEC 2.1), so asking is: record the question, get
  an id back, come back for the answer. Everything below about backoff and the give-up
  timer exists because of that and would not be here if the API could just answer.
*/

type Turn = { role: 'user' | 'assistant'; content: string }

/*
  How long to wait for an answer before saying it failed.

  A help answer off the cheapest tier is two or three seconds. Thirty is long past the
  point where something has gone wrong: the worker is down, or the interactive lane is
  wedged. Either way the owner needs a sentence rather than an indicator that never stops.
*/
const GIVE_UP_MS = 30_000

/*
  Poll fast at first, then ease off. The answer almost always lands in the first second or
  two, and a flat 2s interval would make the common case feel twice as slow as it is.
*/
const STEPS_MS = [400, 400, 600, 800, 1000, 1500]

/*
  Opens the panel from somewhere that is not the launcher.

  A module-level handle rather than a prop threaded Shell → Hub, and rather than context.
  There is exactly one Assistant in the tree, mounted in the shell for the life of the
  session, so there is exactly one thing this could ever mean. The alternative is every
  screen between the shell and the hub row carrying a callback it does not use.

  It exists because a phone has no launcher: the hub row is the only way in below 900px.
  See the note above .asst__launch in app.css.
*/
let show: (() => void) | null = null

export function openAssistant() { show?.() }

export function Assistant({ screen: current }: { screen: string | null }) {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    show = () => setOpen(true)
    return () => { show = null }
  }, [])
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [turns, setTurns] = useState<Turn[]>([])
  const [draft, setDraft] = useState('')
  const [thinking, setThinking] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  const scroller = useRef<HTMLDivElement>(null)
  const input = useRef<HTMLTextAreaElement>(null)

  /*
    The screen the question is being asked from, as the product names it. The shell passes
    the same heading its top bar shows. This used to be read off document.title, which the
    signed-in app never changes, so every question arrived from "Boardium | A board of
    directors for your company" and "the Billing screen you're on" could never be said.
  */
  const screen = useRef<string | null>(current)
  screen.current = current

  useEffect(() => {
    assistant.config()
      .then(c => setSuggestions(c.suggestions ?? []))
      // No suggestions is a fine outcome: the box still works and the caveat still shows.
      .catch(() => setSuggestions([]))
  }, [])

  // Keep the newest message in view as it arrives, including while the indicator is up,
  // or a long answer lands below the fold and reads as nothing having happened.
  useEffect(() => {
    const el = scroller.current
    if (el) el.scrollTop = el.scrollHeight
  }, [turns, thinking, open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  const send = useCallback(async (question: string) => {
    const text = question.trim()
    if (!text || thinking) return

    const next: Turn[] = [...turns, { role: 'user', content: text }]
    setTurns(next)
    setDraft('')
    setFailure(null)
    setThinking(true)

    try {
      const { id } = await assistant.ask(
        next.map(t => ({ role: t.role, content: t.content })),
        screen.current,
        isNativeApp(),
      )

      const answer = await poll(id)
      if (answer.status === 'done' && answer.answer) {
        setTurns([...next, { role: 'assistant', content: answer.answer }])
      } else {
        // The question comes off the thread and goes back in the box. Leaving it on screen
        // above an error reads as though it was asked and ignored, and retyping a
        // paragraph because the worker hiccuped is the insult on top of the injury.
        setFailure(answer.error ?? "I couldn't get an answer just then. Try asking again.")
        setDraft(text)
        setTurns(turns)
      }
    } catch (e) {
      setFailure(e instanceof Error ? e.message : String(e))
      setDraft(text)
      setTurns(turns)
    } finally {
      setThinking(false)
      input.current?.focus()
    }
  }, [turns, thinking])

  return (
    <>
      {open && (
        <div className="asst" role="dialog" aria-label="Boardium Assistant">
          <header className="asst__head">
            <span className="asst__mark" aria-hidden><Sparkle /></span>
            <div className="asst__who">
              <strong>Boardium Assistant</strong>
              <small>Answers about using Boardium</small>
            </div>
            <button
              className="asst__x"
              onClick={() => setOpen(false)}
              aria-label="Close the assistant"
            >
              <Close />
            </button>
          </header>

          <div className="asst__log" ref={scroller}>
            {turns.length === 0 && (
              <div className="asst__empty">
                <p>
                  Ask me anything about setting your company up, what the board does with
                  what you give it, or where something lives.
                </p>
                <div className="asst__chips">
                  {suggestions.map(s => (
                    <button key={s} className="asst__chip" onClick={() => void send(s)}>
                      {s}
                    </button>
                  ))}
                </div>
                <p className="asst__caveat">
                  I explain how the software works. I can't see your agenda, your meetings
                  or your numbers, and I can't change anything. For a question about your
                  business, ask the board from Home.
                </p>
              </div>
            )}

            {turns.map((t, i) => (
              <div key={i} className={`asst__msg asst__msg--${t.role}`}>
                {t.role === 'assistant' && (
                  <span className="asst__avatar" aria-hidden><Sparkle /></span>
                )}
                <div className="asst__bubble"><Prose raw={t.content} /></div>
              </div>
            ))}

            {thinking && (
              <div className="asst__msg asst__msg--assistant">
                <span className="asst__avatar" aria-hidden><Sparkle /></span>
                <div className="asst__bubble asst__typing" aria-label="Thinking">
                  <i /><i /><i />
                </div>
              </div>
            )}

            {failure && <div className="asst__fail" role="alert">{failure}</div>}
          </div>

          <form
            className="asst__compose"
            onSubmit={e => { e.preventDefault(); void send(draft) }}
          >
            <textarea
              ref={input}
              className="asst__box"
              value={draft}
              rows={1}
              placeholder="Ask a question"
              aria-label="Ask the assistant"
              onChange={e => setDraft(e.target.value)}
              // Enter sends, Shift+Enter breaks the line. A chat box that needs a mouse to
              // send is a chat box nobody uses twice.
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(draft) }
              }}
            />
            <button
              className="asst__send"
              type="submit"
              disabled={thinking || draft.trim() === ''}
              aria-label="Send"
            >
              <Send />
            </button>
          </form>
        </div>
      )}

      <button
        className={`asst__launch${open ? ' is-open' : ''}`}
        onClick={() => { setOpen(o => !o); setTimeout(() => input.current?.focus(), 60) }}
        aria-expanded={open}
        aria-label={open ? 'Close the assistant' : 'Ask the assistant'}
      >
        {open ? <Close /> : <Sparkle />}
      </button>
    </>
  )
}

/**
 * Comes back when there is an answer, or when it has waited long enough to say there
 * isn't one. Never rejects on a poll that failed: one dropped response mid-thread is not
 * worth ending the wait over, and the give-up timer catches a server that has really gone.
 */
async function poll(id: string): Promise<AssistantAnswer> {
  const deadline = Date.now() + GIVE_UP_MS

  for (let i = 0; Date.now() < deadline; i++) {
    await new Promise(r => setTimeout(r, STEPS_MS[Math.min(i, STEPS_MS.length - 1)]))
    try {
      const got = await assistant.answer(id)
      if (got.status !== 'pending') return got
    } catch {
      // Keep waiting. The deadline is the thing that ends this.
    }
  }

  return {
    status: 'failed',
    answer: null,
    error: "That took longer than it should have. Try asking again.",
  }
}

/* ── Glyphs ───────────────────────────────────────────────────────────────── */
/* Inline rather than from a sprite: three shapes used nowhere else in the product. */

/*
  Stroke, not fill, and 1.5 rather than 1.7.

  Every glyph in this product is a hairline that inherits currentColor, which is what the
  note above the shell's icons says and what keeps the nav, the tab bar and the row
  chevrons looking like one set. A solid sparkle would be the only filled icon in the
  product and would read as a sticker somebody dropped on the page.
*/
const stroke = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
}

/* The four-point star with its small companion: the shape everything else on the web has
   settled on for "ask the machine", which is worth more here than being distinctive. */
function Sparkle() {
  return (
    <svg {...stroke} aria-hidden>
      <path d="M12 3.5 13.8 9l5.5 1.8-5.5 1.8L12 18l-1.8-5.4L4.7 10.8 10.2 9 12 3.5Z" />
      <path d="M18.5 3v3M20 4.5h-3" />
    </svg>
  )
}

function Close() {
  return (
    <svg {...stroke} aria-hidden>
      <path d="m6 6 12 12M18 6 6 18" />
    </svg>
  )
}

function Send() {
  return (
    <svg {...stroke} aria-hidden>
      <path d="M21 3 10.5 13.5M21 3l-6.8 18-3.7-7.5L3 9.8 21 3Z" />
    </svg>
  )
}
